import { ChangeEvent, useState } from 'react'
import { CircularProgress } from '@mui/material'
import CustomInput from '../../components/TextInput'
import CustomButton from '../../components/Button'
import {
    containsLowercase,
    containsNotBlankSpace,
    containsNumber,
    containsSpecialCharacter,
    containsUppercase,
    validateEmail,
    validatePassword,
} from '../../helpers/utils'
import { TRegisterInfo } from '../../hooks/useFormRegister'
import AuthService from '../../services/AuthService'
import { useSnackbar } from 'notistack'
import { VerifyExistenceResponse } from '../../models/auth'
import { Tooltip } from 'react-tooltip'
import { CheckRules, DEFAULT_CHECKS, PasswordTooltip } from '../../components/Tooltip'
import AdminService from '../../services/AdminService'
import { useNavigate } from 'react-router-dom'

const DEFAULT_ERRORS = {
    email: '',
    password: '',
    repeatPassword: '',
}

const DEFAULT_INFO = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    repeatPassword: '',
}
export type CreateAdminInfoType = typeof DEFAULT_INFO

interface Props {
    handleClose: () => void
}
const NewAdminForm = ({ handleClose }: Props) => {
    const [info, setInfo] = useState<CreateAdminInfoType>(DEFAULT_INFO)
    const [pending, setPending] = useState<boolean>(false)
    const [errors, setErrors] = useState<typeof DEFAULT_ERRORS>(DEFAULT_ERRORS)
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const [checkedRules, setCheckedRules] = useState<CheckRules>(DEFAULT_CHECKS)

    const handleCheckPassword = (password: string) => {
        setCheckedRules((prev) => ({
            ...prev,
            lowerCase: containsLowercase(password),
            upperCase: containsUppercase(password),
            number: containsNumber(password),
            specialCharacter: containsSpecialCharacter(password),
            long: password.length >= 8,
            notBlankSpace: containsNotBlankSpace(password),
        }))
    }

    const handleOnChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
        handleCheckPassword(event.target.value)
        setInfo((prev) => ({ ...prev, password: event.target.value }))
    }

    const handleOnChangeRepeatPassword = (event: ChangeEvent<HTMLInputElement>) => {
        setCheckedRules((prev) => ({
            ...prev,
            samePassword: event.target.value === info.password,
        }))
        setInfo((prev) => ({ ...prev, repeatPassword: event.target.value }))
    }

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInfo((prev) => ({ ...prev, [event.target.name]: event.target.value }))
    }

    const handleButtonDisabled = () => {
        return Object.values(info).some((x) => x === '') || pending
    }

    const validateInfo = () => {
        const newErrors = {
            email: validateEmail(info.email) ? '' : 'El email ingresado no es válido.',
            password: !validatePassword(info.password) ? 'La contraseña no es válida' : '',
            repeatPassword:
                info.password === info.repeatPassword ? '' : 'Las contraseñas no coinciden.',
        }

        setErrors(newErrors)
        return Object.values(newErrors).every((x) => x === '')
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return

        setPending(true)
        const response = await new AuthService().verifyExistence({
            email: info.email,
            documentValue: 'dni',
            documentType: 'DNI',
        } as TRegisterInfo)

        if (response.status !== 200) {
            enqueueSnackbar('Error en la solicitud.', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        } else {
            const data: VerifyExistenceResponse = response.data
            const newErrors = {
                password: '',
                repeatPassword: '',
                email: !data.emailAvailable
                    ? 'Ya existe una cuenta asociada al email ingresado'
                    : '',
            }
            setErrors(newErrors)
            if (Object.values(newErrors).some((x) => x !== '')) return

            const createdResponse = await new AdminService().create(info)
            if (createdResponse.status !== 200) {
                enqueueSnackbar('Error en la solicitud.', {
                    variant: 'error',
                    autoHideDuration: 6000,
                })
            } else {
                enqueueSnackbar('Administrador creado correctamente.', {
                    variant: 'success',
                    autoHideDuration: 6000,
                })
            }
        }
        handleClose()
        setTimeout(() => navigate(0), 1500)
    }

    return (
        <>
            <div className="w-[65vw] md:w-[30vw] flex flex-col gap-2 md:gap-4">
                <span className="text-primary text-xl">Crear administrador</span>
                <div className="flex flex-col gap-2 md:gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                        <CustomInput
                            type="text"
                            label="Nombre"
                            placeholder="Ingresá el nombre"
                            name="firstName"
                            value={info.firstName}
                            onChange={handleOnChange}
                        />
                        <CustomInput
                            type="text"
                            label="Apellido"
                            placeholder="Ingresá el apellido"
                            name="lastName"
                            value={info.lastName}
                            onChange={handleOnChange}
                        />
                    </div>
                    <CustomInput
                        type="text"
                        label="Email"
                        placeholder="Ingresá el email"
                        name="email"
                        value={info.email}
                        onChange={handleOnChange}
                        errorText={errors.email}
                    />
                    <CustomInput
                        type="password"
                        label="Contraseña"
                        placeholder="Ingresá la contraseña"
                        name="password"
                        value={info.password}
                        onChange={handleOnChangePassword}
                        errorText={errors.password}
                        className="tooltip"
                    />
                    <CustomInput
                        type="password"
                        label="Repetir contraseña"
                        placeholder="Repetí la contraseña"
                        name="repeatPassword"
                        value={info.repeatPassword}
                        onChange={handleOnChangeRepeatPassword}
                        errorText={errors.repeatPassword}
                        className="tooltip"
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <CustomButton
                        className="h-[36px]"
                        onClick={handleClose}
                        text="Cerrar"
                        secondary
                    />
                    <CustomButton
                        className="h-[36px]"
                        onClick={handleSubmit}
                        disabled={handleButtonDisabled()}
                    >
                        {!pending ? (
                            'Enviar'
                        ) : (
                            <CircularProgress
                                className="text-primary"
                                size={24}
                            />
                        )}
                    </CustomButton>
                </div>
            </div>
            <Tooltip
                anchorSelect=".tooltip"
                place="right"
                className="shadow-custom bg-secondary text-white rounded-md w-[300px]"
            >
                <PasswordTooltip checked={checkedRules} />
            </Tooltip>
        </>
    )
}

export default NewAdminForm
